import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
  Theme,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

interface ThemeContextType {
  mode: "light" | "dark";
  toggleTheme: () => void;
  theme: Theme;
  setThemeDirection: (isRTL: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within a ThemeProvider");
  return context;
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<"light" | "dark">(() => {
    const saved = localStorage.getItem("aitools-theme");
    return (saved as "light" | "dark") || "light";
  });

  const [direction, setDirection] = useState<"ltr" | "rtl">(() => {
    const savedLang = localStorage.getItem("language");
    return savedLang === "ar" ? "rtl" : "ltr";
  });

  const theme = createTheme({
    direction,
    typography: {
      fontFamily: '"Nunito", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
      button: { textTransform: "none", fontWeight: 600 },
    },
    palette: {
      mode,
      primary: {
        main:         mode === "dark" ? "#FFB06A" : "#FF8428",
        light:        mode === "dark" ? "#FFD0A3" : "#FFB06A",
        dark:         mode === "dark" ? "#D96718" : "#D95F13",
        contrastText: mode === "dark" ? "#1B1511" : "#FFFFFF",
      },
      secondary: {
        main:         mode === "dark" ? "#E8D4C0" : "#6F5A4C",
        contrastText: mode === "dark" ? "#1B1511" : "#FFFFFF",
      },
      background: {
        default: mode === "dark" ? "#171210" : "#F3E8DC",
        paper:   mode === "dark" ? "#241B17" : "#FFF8EF",
      },
      text: {
        primary:   mode === "dark" ? "#FFF8EF" : "#171615",
        secondary: mode === "dark" ? "#D6C2AE" : "#71665D",
      },
      success: { main: "#10B981" },
      error:   { main: "#EF4444" },
      warning: { main: "#F59E0B" },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(255,255,255,0.72)",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { backgroundImage: "none" },
        },
      },
    },
  });

  const toggleTheme = () => {
    const next = mode === "light" ? "dark" : "light";
    setMode(next);
    localStorage.setItem("aitools-theme", next);
  };

  useEffect(() => {
    localStorage.setItem("aitools-theme", mode);
    document.body.dataset.theme = mode;
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  const setThemeDirection = (isRTL: boolean) => setDirection(isRTL ? "rtl" : "ltr");

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, theme, setThemeDirection }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
